import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Dispatch, SetStateAction, useState } from 'react';
import { useForm, useFieldArray, SubmitHandler } from 'react-hook-form';
import {
  GPanelPermissionsModel,
  SharedDriveModel,
} from '../../../../../../../types';
import ChangeLogDialog from '../../../../../../shared/change-log-dialog';
import { handleErrors } from '../../../../../../utils/errors';
import SharedDialog from '../../../../../../shared/shared-dialog';
import {
  UserKeys,
  getSharedDriveAcls,
  updateSharedDriveAcls,
} from '../../../../../../api/users-api';
import { useStore } from '../../../../../state/zustand-store';
import {
  DriveRoles,
  isAbleToSeeUIElement,
} from '../../../../../../utils/roles/client-roles';
import { useAnalytics } from '../../../../../../shared/analytics/create-events';
import { Messages } from '../../../../../../utils/message-constants';
import SharedDriveMembersEditor from './shared-drive-members-editor';
import {
  getSharedDriveAnalyticsEvent,
  getSharedDrivePage,
} from './shared-drive-analytics';
import {
  SHARED_DRIVE_MIME_TYPE,
  SharedDriveMembersFormValues,
} from './shared-drive-members';

import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import ContentCopy from '@mui/icons-material/ContentCopy';

interface SharedDrivesPermissionDialogProps {
  open: boolean;
  handleClose: () => void;
  userEmail: string;
  selectedDrive: SharedDriveModel;
  setSelectedDrive: Dispatch<SetStateAction<SharedDriveModel | null>>;
  driveRoles: string[];
  changeLogRoles: string[];
}

export default function SharedDrivesPermissionDialog({
  open,
  handleClose,
  userEmail,
  changeLogRoles,
  selectedDrive,
  setSelectedDrive,
}: SharedDrivesPermissionDialogProps) {
  const [openChangeLogDialog, setOpenChangeLogDialog] = useState(false);
  const [changeLogMessage, setChangeLogMessage] = useState('');
  const queryClient = useQueryClient();
  const { setSnackbar } = useStore();
  const { createEvent } = useAnalytics();

  const { data, isLoading, isFetching } = useQuery({
    queryKey: [UserKeys.DriveAcls, userEmail, selectedDrive.id],
    queryFn: () =>
      getSharedDriveAcls(selectedDrive.id, userEmail, SHARED_DRIVE_MIME_TYPE),
    meta: {
      errorMessage: Messages.Errors.Network.Loading(
        'the drive share permission settings'
      ),
    },
  });

  const { mutate, isPending: isSharing } = useMutation({
    mutationFn: (formData: GPanelPermissionsModel[]) =>
      updateSharedDriveAcls(
        selectedDrive.id,
        userEmail,
        SHARED_DRIVE_MIME_TYPE,
        formData,
        selectedDrive.name,
        changeLogMessage
      ),
    onSuccess: (queued) => {
      setSnackbar(
        queued
          ? {
              open: true,
              severity: 'info',
              message: Messages.SharedDrives.MembersTaskStarted,
            }
          : {
              open: true,
              severity: 'success',
              message: Messages.Success.Updated(
                'Drive share permission settings'
              ),
            }
      );

      queryClient.invalidateQueries({
        queryKey: [UserKeys.DriveAcls],
      });

      setChangeLogMessage('');
      handleClose();
      reset();
    },
    onError: (error) => {
      handleErrors({
        error,
        snackbarMessage: Messages.Errors.Network.Updating(
          'the drive share permission settings'
        ),
      });
    },
    onSettled: () => {
      const url = window.location.href.toLowerCase();
      const isSharedDrives = url.includes('shared');
      const isDriveExplorer = url.includes('explorer');

      const { name, properties } = getSharedDriveAnalyticsEvent(
        'update_drive_shares',
        getSharedDrivePage(isSharedDrives, isDriveExplorer)
      );
      createEvent(name, properties);

      setOpenChangeLogDialog(false);
    },
  });

  const filteredData = data?.filter((a) => a.userEmail);
  const sortedData =
    filteredData?.sort((a, b) => a.userEmail!.localeCompare(b.userEmail!)) ||
    [];
  const emailSheetCopy =
    sortedData
      ?.map((acl) => acl.userEmail)
      .filter((userEmail) => userEmail !== undefined)
      .join(', ') || '';

  const form = useForm<SharedDriveMembersFormValues>({
    values: {
      aclResources: sortedData,
    },
  });
  const {
    control,
    handleSubmit,
    reset,
    getValues,
    formState: { isDirty },
  } = form;

  const fieldArray = useFieldArray({
    control,
    name: 'aclResources',
  });

  function mutateData() {
    // acls that have been added or changed
    const addedOrChangedData = getValues('aclResources').filter(
      (acl) => acl.changeType
    );

    // acls that have been deleted
    const allEmails = data?.map((acl) => acl.userEmail);
    const formEmails = getValues('aclResources').map((acl) => acl.userEmail);
    const deletedEmails = allEmails?.filter(
      (email) => !formEmails.includes(email)
    );
    const deletedData = data?.filter(
      (acl) => deletedEmails?.includes(acl.userEmail)
    );

    const modifiedDeletedData =
      deletedData?.map((acl) => {
        return {
          ...acl,
          changeType: 'delete' as 'delete' | 'add' | 'change',
          toBeDeleted: true,
        };
      }) || [];
    return [...addedOrChangedData, ...modifiedDeletedData];
  }

  const onSubmit: SubmitHandler<{
    aclResources: GPanelPermissionsModel[];
  }> = () => {
    if (
      isAbleToSeeUIElement(
        changeLogRoles,
        DriveRoles.CanUpdateSharedDrivePermissions.value
      )
    ) {
      setOpenChangeLogDialog(true);
    } else {
      const formData = mutateData();
      mutate(formData);
    }
  };

  return (
    <SharedDialog
      open={open}
      handleClose={() => {
        handleClose();
        setSelectedDrive(null);
      }}
      title={`Manage Members`}
      actingAs={userEmail}
      dialogContent={
        <Stack>
          <SharedDriveMembersEditor
            form={form}
            fieldArray={fieldArray}
            originalMembers={sortedData}
            newMemberDefaults={{
              docId: selectedDrive.id,
              docOwner: userEmail,
              docTitle: selectedDrive.name,
            }}
            isLoading={isLoading || isFetching}
            listHeader={
              <Stack
                flexDirection="row"
                alignItems="center"
                justifyContent="space-between"
              >
                <Typography>Who has access:</Typography>
                {isLoading || isFetching ? (
                  <></>
                ) : (
                  <Stack
                    flexDirection="row"
                    alignItems="center"
                    alignSelf="flex-end"
                  >
                    <Tooltip arrow title="Copy member emails">
                      <IconButton
                        onClick={async (e) => {
                          e.stopPropagation();
                          await navigator.clipboard.writeText(emailSheetCopy);
                          setSnackbar({
                            open: true,
                            severity: 'info',
                            message:
                              'Shared drive members’ email addresses have been copied to the clipboard.',
                          });
                        }}
                      >
                        <ContentCopy fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                )}
              </Stack>
            }
          />
          <ChangeLogDialog
            open={openChangeLogDialog}
            handleClose={() => setOpenChangeLogDialog(false)}
            changeLogMessage={changeLogMessage}
            setChangeLogMessage={setChangeLogMessage}
            mutate={() => {
              const formData = mutateData();
              mutate(formData);
            }}
            mutateLoading={isSharing}
          />
        </Stack>
      }
      primaryButton={{
        title: 'Save Changes',
        action: handleSubmit(onSubmit),
        loading: isSharing,
        disabled: !isDirty || isSharing,
      }}
    />
  );
}
